import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MdMenu, MdClose } from 'react-icons/md';
import { MdLanguage } from 'react-icons/md';
import logo from '../img/harmanlogo2.png';
import { useLang } from '../contexts/LanguageContext';
import { translations, LANGUAGES } from '../utils/translations';

const Header = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { lang, setLang } = useLang();
  const t = translations[lang].header;
  const langRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navItems = t.nav;

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      scrolled
        ? 'bg-gray-950/95 backdrop-blur-xl shadow-[0_1px_0_rgba(255,255,255,0.04)]'
        : 'bg-gray-950'
    }`}>
      <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">

        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src={logo}
            alt="Harman Play"
            className="h-8 brightness-0 invert opacity-90 hover:opacity-60 transition-opacity duration-200"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`relative text-[11px] font-bold tracking-widest uppercase transition-colors duration-200 group ${
                location.pathname === to ? 'text-white' : 'text-gray-500 hover:text-white'
              }`}
            >
              {label}
              <span className={`absolute -bottom-1 left-0 h-px bg-headingColor transition-all duration-300 ${
                location.pathname === to ? 'w-full' : 'w-0 group-hover:w-full'
              }`} />
            </Link>
          ))}

          {/* Language Switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(v => !v)}
              className="flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase text-gray-500 hover:text-white transition-colors duration-200"
              aria-label="Dil seç"
            >
              <MdLanguage className="text-base" />
              <span>{LANGUAGES.find(l => l.code === lang)?.flag}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-8 bg-gray-900 border border-white/10 rounded-lg overflow-hidden shadow-xl min-w-[120px]">
                {LANGUAGES.map(({ code, name, flag }) => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-[11px] font-bold tracking-widest uppercase transition-colors ${
                      lang === code ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span>{flag}</span> {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Mobile right: lang + burger */}
        <div className="md:hidden flex items-center gap-3">
          <div className="relative" ref={null}>
            <button
              onClick={() => setLangOpen(v => !v)}
              className="flex items-center gap-1 text-gray-500 hover:text-white transition-colors"
              aria-label="Dil seç"
            >
              <MdLanguage className="text-lg" />
              <span className="text-sm">{LANGUAGES.find(l => l.code === lang)?.flag}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-8 bg-gray-900 border border-white/10 rounded-lg overflow-hidden shadow-xl min-w-[120px] z-50">
                {LANGUAGES.map(({ code, name, flag }) => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-[11px] font-bold tracking-widest uppercase transition-colors ${
                      lang === code ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span>{flag}</span> {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="text-gray-500 hover:text-white text-xl transition-colors"
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Menü"
          >
            {menuOpen ? <MdClose /> : <MdMenu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-gray-950 border-t border-white/[0.05] px-6 pb-6 flex flex-col gap-1">
          {navItems.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`text-[11px] font-bold tracking-widest uppercase py-3.5 border-b border-white/[0.05] transition-colors ${
                location.pathname === to ? 'text-white' : 'text-gray-500'
              }`}
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;
